/**
 * ATAJOS DE TECLADO del editor.
 *
 * Se resuelven con una funcion PURA —de evento a intencion— para poder
 * comprobarlos sin navegador. La parte que toca el DOM se queda en el
 * componente, que es donde tiene sentido.
 *
 * Mover con las flechas no es un lujo: con el raton no se puede colocar algo
 * con precision de milimetro, y en una zona de 280 mm un milimetro de mas se ve
 * en la prenda.
 */

export type Intencion =
  | { tipo: 'deshacer' }
  | { tipo: 'rehacer' }
  | { tipo: 'borrar' }
  | { tipo: 'duplicar' }
  | { tipo: 'deseleccionar' }
  | { tipo: 'mover'; dx: number; dy: number }
  | { tipo: 'agrupar' }
  | { tipo: 'desagrupar' }
  | { tipo: 'seleccionar-todo' };

export interface EventoTecla {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  /** Donde ocurrio: si es un campo de texto, el atajo NO se aplica. */
  enCampoDeTexto?: boolean;
}

/** Salto fino y salto grueso, en milimetros. */
export const PASO_FINO_MM = 1;
export const PASO_GRUESO_MM = 10;

export function interpretar(evento: EventoTecla): Intencion | null {
  // Dentro de un campo de texto manda el campo: Ctrl+Z deshace lo escrito, no
  // el diseno, y Supr borra un caracter, no la capa.
  if (evento.enCampoDeTexto) return null;

  const mandos = evento.ctrlKey || evento.metaKey;

  if (mandos) {
    const k = evento.key.toLowerCase();
    if (k === 'z' && !evento.shiftKey) return { tipo: 'deshacer' };
    if ((k === 'z' && evento.shiftKey) || k === 'y') return { tipo: 'rehacer' };
    if (k === 'd') return { tipo: 'duplicar' };
    if (k === 'a') return { tipo: 'seleccionar-todo' };
    // Ctrl+G agrupa y Ctrl+Shift+G deshace el grupo, como en cualquier editor.
    if (k === 'g') return evento.shiftKey ? { tipo: 'desagrupar' } : { tipo: 'agrupar' };
    return null;
  }

  if (evento.key === 'Delete' || evento.key === 'Backspace') return { tipo: 'borrar' };
  if (evento.key === 'Escape') return { tipo: 'deseleccionar' };

  const salto = evento.shiftKey ? PASO_GRUESO_MM : PASO_FINO_MM;
  switch (evento.key) {
    case 'ArrowLeft':
      return { tipo: 'mover', dx: -salto, dy: 0 };
    case 'ArrowRight':
      return { tipo: 'mover', dx: salto, dy: 0 };
    case 'ArrowUp':
      return { tipo: 'mover', dx: 0, dy: -salto };
    case 'ArrowDown':
      return { tipo: 'mover', dx: 0, dy: salto };
    default:
      return null;
  }
}

/** Si un elemento del DOM se comporta como campo de texto. */
export function esCampoDeTexto(destino: EventTarget | null): boolean {
  const el = destino as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const etiqueta = el.tagName.toLowerCase();
  return etiqueta === 'input' || etiqueta === 'textarea' || etiqueta === 'select' || el.isContentEditable === true;
}
